import React, { useCallback, useRef, useState } from "react";
import VisualizationModal from "../../../components/chatNewInterface/interfaceComponents/visualizationModal/VisualizationModal";
import type { MessageVisualizationBlock } from "../../../components/chatNewInterface/interfaceComponents/visualizationModal/VisualizationModal";
import type { MessageItem } from "../../companion/companionMessage.types";
import useLearningLlmStreamV2 from "../../../hooks/useLearningLlmStreamV2";

export type { MessageVisualizationBlock };

interface VisualizationModalContainerProps {
  visible: boolean;
  block: MessageVisualizationBlock;
  vizChatId: string | null;
  onVizChatId: (chatId: string) => void;
  contentNodeId: string;
  messages: MessageItem[];
  onMessagesChange: (updater: (prev: MessageItem[]) => MessageItem[]) => void;
  onClose: () => void;
}

const VisualizationModalContainer = ({
  visible,
  block,
  vizChatId,
  onVizChatId,
  contentNodeId,
  messages,
  onMessagesChange,
  onClose,
}: VisualizationModalContainerProps) => {
  const { startStream, isStreaming } = useLearningLlmStreamV2();
  const [inputText, setInputText] = useState("");
  const activeChatIdRef = useRef<string | null>(vizChatId);
  const streamingIdRef = useRef<string | null>(null);

  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || isStreaming) return;
    setInputText("");

    const userMsgId = `viz-user-${Date.now()}`;
    const aiMsgId = `viz-ai-${Date.now()}`;
    streamingIdRef.current = aiMsgId;

    const userMsg: MessageItem = { id: userMsgId, sender: "human", content: text };
    const aiMsg: MessageItem = { id: aiMsgId, sender: "ai", content: "" };

    onMessagesChange((prev) => [...prev, userMsg, aiMsg]);

    try {
      await startStream(text, {
        chatId: activeChatIdRef.current ?? undefined,
        contentNodeId,
        visualizationFocus: block.visualizationDescription ?? block.visualizationType,
        skipChatLookup: true,
        onChatId: (nextChatId) => {
          activeChatIdRef.current = nextChatId;
          onVizChatId(nextChatId);
        },
        onChunk: ({ text: chunk, isFinalChunk }) => {
          const id = streamingIdRef.current;
          if (!id) return;
          onMessagesChange((prev) =>
            prev.map((m) => (m.id === id ? { ...m, content: `${m.content ?? ""}${chunk}` } : m))
          );
          if (isFinalChunk) streamingIdRef.current = null;
        },
        onComplete: ({ event }) => {
          if (event?.chatId) activeChatIdRef.current = event.chatId;
          streamingIdRef.current = null;
        },
        onError: () => {
          streamingIdRef.current = null;
        },
      });
    } catch {
      streamingIdRef.current = null;
    }
  }, [inputText, isStreaming, contentNodeId, block, startStream, onMessagesChange]);

  return (
    <VisualizationModal
      visible={visible}
      block={block}
      messages={messages}
      inputText={inputText}
      isStreaming={isStreaming}
      onInputChange={setInputText}
      onSend={handleSend}
      onClose={onClose}
    />
  );
};

export default VisualizationModalContainer;
